import { Calculator } from "lucide-react";
import { StudiesTooltip, lineSourceCns } from "./StudiesTooltip";

const n = (v, d = 0) => Number(v || 0).toLocaleString("es-ES", { maximumFractionDigits: d, minimumFractionDigits: 0 });

function Step({ label, value, sub, tone = "slate", testid }) {
  const tones = { slate: "bg-white border-slate-200 text-slate-900", rose: "bg-rose-50 border-rose-200 text-rose-800", emerald: "bg-emerald-600 border-emerald-600 text-white" };
  return (
    <div className={`rounded-lg border px-3 py-2 min-w-[96px] ${tones[tone]}`} data-testid={testid}>
      <div className={`text-[10px] uppercase tracking-wider font-bold ${tone === "emerald" ? "text-emerald-100" : "text-slate-500"}`}>{label}</div>
      <div className="font-mono-num text-lg font-bold leading-tight">{value}</div>
      {sub && <div className={`text-[10px] leading-tight ${tone === "emerald" ? "text-emerald-100" : "text-slate-500"}`}>{sub}</div>}
    </div>
  );
}

const Op = ({ children }) => <span className="text-xl font-bold text-slate-400 px-0.5">{children}</span>;

export function OrderCalcBreakdown({ line, demand, avgMonths, testid }) {
  const c = line.calc;
  if (!c) return null;
  const final = line.units;
  const months = demand?.months_covered ?? avgMonths;
  const studies = demand?.studies_used;
  const histLabel = `${n(months, 2)} ${Number(months) === 1 ? "mes" : "meses"}${studies ? ` · ${studies} ${studies === 1 ? "estudio" : "estudios"}` : ""}`;
  const marcaNote = line.marca_conversion_from?.length ? "Incluye unidades de marca antes de aplicar el % de conversión." : null;
  return (
    <div className="px-6 py-4 border-b border-sky-200/60" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">
        <Calculator className="h-3.5 w-3.5" /> Cómo se calcula el pedido
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <Step label="Venta media/mes" value={`${n(c.pm, 2)} uds`} testid={`${testid}-pm`}
          sub={<StudiesTooltip demand={demand} cns={lineSourceCns(line)} note={marcaNote} testid={`${testid}-hist`}>histórico usado: {histLabel}</StudiesTooltip>} />
        <Op>×</Op>
        <Step label="Cobertura" value={`${c.days} días`} sub={`= ${n(c.days / 30, 2)} meses`} testid={`${testid}-days`} />
        <Op>=</Op>
        <Step label="Necesitas" value={`${n(c.theoretical)} uds`} sub="redondeado" testid={`${testid}-theoretical`} />
        <Op>−</Op>
        <Step label="Stock este CN" value={`${n(c.stock_cn)} uds`} tone={c.stock_cn ? "rose" : "slate"} testid={`${testid}-stock-cn`} />
        <Op>−</Op>
        <Step label="Stock genérico otros" value={`${n(c.stock_other)} uds`} sub="misma molécula" tone={c.stock_other ? "rose" : "slate"} testid={`${testid}-stock-other`} />
        <Op>=</Op>
        <Step label="A pedir" value={`${n(c.raw)} uds`} tone={line.is_override || c.raw !== final ? "slate" : "emerald"} testid={`${testid}-raw`} />
        {(c.picking || line.is_override) && c.raw !== final && (
          <>
            <Op>→</Op>
            <Step label={line.is_override ? "Ajustado a mano" : "Picking"} value={`${n(final)} uds`}
              sub={line.is_override ? "con el lápiz" : "1 ud si < 5 · 2 si ≥ 5"} tone="emerald" testid={`${testid}-final`} />
          </>
        )}
      </div>
      <p className="text-[11px] text-slate-500 mt-2">
        Venta media/mes = unidades dispensadas en el histórico usado ({histLabel}) ÷ esos meses. Se resta el stock de tu fichero para no pedir lo que ya tienes en estantería.
      </p>
    </div>
  );
}
